/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import ReactSelectBase from 'react-select';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { toast } from 'react-hot-toast';
import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';

import taskService from '../../../../services/TaskService';
import employeeService from '../../../../services/EmployeeService';

import { AttachmentList, DetailItem, PersonAvatar, StatusIcon } from './TotalTaskPopupEditingItem';
import { formatAuditValue, formatDateDDMMYYYY, getDisplayName, priorityColors, statusColors } from '../helper/helper';
import {
  mapTaskToRow,
  TASK_STATUSES,
  TASK_PRIORITIES,
  TASK_TYPES,
  TASK_PENDING_REASONS,
  TASK_CRITICAL_REASONS,
  createsDependencyCycle
} from '../../../MyTask/helper/helper';
import SupportingStaffSelect from '../../../MyTask/SupportingStaffSelect';
import {
  sameSupportingStaffOptions,
  splitSupportingStaffOptions,
  taskSupportingStaffOptions
} from '../../../MyTask/helper/supportingStaff';

import Draggable from 'react-draggable';

const DraggablePaper = (props) => {
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#total-task-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds="parent"
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const dependencySelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (base) => ({ ...base, minHeight: 40 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 240,
    overflowY: 'auto',
    overscrollBehavior: 'contain'
  })
};

const ReactSelect = (props) => (
  <ReactSelectBase
    {...props}
    isSearchable
    maxMenuHeight={240}
    menuPlacement="auto"
    menuPosition="fixed"
    menuShouldScrollIntoView={false}
  />
);

const InputLabel = ({ children }) => (
  <Typography component="label" variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
    {children}
  </Typography>
);

// select is using ReactSelect from
const Select = ({ children, value, onChange, multiple = false, label, labelId, disabled = false }) => {
  const options = React.Children.toArray(children)
    .filter((child) => child?.props)
    .map((child) => ({ value: child.props.value, label: child.props.children }));
  const selectedValue = multiple
    ? (value || []).map(
        (selectedId) =>
          options.find((option) => String(option.value) === String(selectedId)) || {
            value: selectedId,
            label: String(selectedId)
          }
      )
    : options.find((option) => String(option.value) === String(value)) || null;

  return (
    <ReactSelect
      inputId={labelId}
      aria-label={label}
      options={options}
      value={selectedValue}
      isMulti={multiple}
      closeMenuOnSelect={!multiple}
      hideSelectedOptions={false}
      isDisabled={disabled}
      styles={dependencySelectStyles}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      onChange={(selected) =>
        onChange({
          target: {
            value: multiple ? (selected || []).map((option) => option.value) : selected?.value ?? ''
          }
        })
      }
    />
  );
};

export const Popup = ({
  row,
  onApplyChanges,
  onCancelChanges,
  open,
  taskTabIndex,
  setTaskTabIndex,
  onUpdateRow,
  classOptions = [],
  dependencyOptions = []
}) => {
  const [newComment, setNewComment] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Assigned');
  const [selectedPriority, setSelectedPriority] = useState('Normal');
  const [selectedType, setSelectedType] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedDependencyIds, setSelectedDependencyIds] = useState([]);
  const [selectedDueDate, setSelectedDueDate] = useState('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [selectedSupportingStaffOptions, setSelectedSupportingStaffOptions] = useState([]);
  const [selectedDescription, setSelectedDescription] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedEstimatedHours, setSelectedEstimatedHours] = useState('');
  const [actualHours, setActualHours] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [externalContacts, setExternalContacts] = useState([]);
  const [pendingReason, setPendingReason] = useState('');
  const [pendingComment, setPendingComment] = useState('');
  const [criticalReason, setCriticalReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [isWorking, setIsWorking] = useState(false);
  const [selectedStartTime, setSelectedStartTime] = useState('');

  const initializedTask = useRef();
  useEffect(() => {
    const taskData = row?._raw || row;
    if (initializedTask.current === taskData) return;
    initializedTask.current = taskData;
    setSelectedStatus(row?.status);
    setSelectedPriority(row?.priority || 'Normal');
    setSelectedType(row?.type || '');
    setSelectedClassId(row?._raw?.class?.id || '');
    setSelectedDependencyIds(row?.dependencyIds || []);
    setSelectedDueDate(toDateInputValue(row?._raw?.dueDate));
    setSelectedAssigneeId(row?._raw?.assignedTo?.[0]?.employeeId?._id || row?._raw?.assignedTo?.[0]?.employeeId || '');
    setSelectedSupportingStaffOptions(taskSupportingStaffOptions(row?._raw));
    setSelectedDescription(row?._raw?.description || row?.description || '');
    setSelectedLocation(row?._raw?.location || row?.location || '');
    setSelectedEstimatedHours(row?._raw?.estimatedHours ?? row?.estimatedHours ?? '');
    setActualHours(row?.actualHours ?? row?._raw?.actualHours ?? '');
    setPendingReason(row?._raw?.workflow?.pendingReason || '');
    setPendingComment(row?._raw?.workflow?.pendingComment || '');
    setCriticalReason(row?._raw?.workflow?.criticalReason || '');
    setCancellationReason(row?._raw?.workflow?.cancellationReason || '');
    setSelectedStartTime(row?._raw?.startTime || '');
    setCommentFiles([]);
  }, [row]);

  useEffect(() => {
    const loadStaffOptions = async () => {
      // eslint-disable-next-line no-undef
      const [employeesResult, contactsResult] = await Promise.allSettled([
        employeeService.getActiveEmployees(),
        taskService.getExternalContacts()
      ]);
      if (employeesResult.status === 'fulfilled') setEmployeeOptions(employeesResult.value?.data?.data || []);
      else toast.error('Could not load employees');
      if (contactsResult.status === 'fulfilled') setExternalContacts(contactsResult.value?.data?.data || []);
    };
    loadStaffOptions();
  }, []);

  if (!row) return null;

  const isSubmittedForApproval = row.status === 'Submitted for Approval';
  // eslint-disable-next-line no-undef
  const currentUserId = localStorage.getItem('userId');
  const creatorId = row?._raw?.createdBy?._id || row?._raw?.createdBy;
  const isTaskCreator = String(creatorId || '') === String(currentUserId || '');

  const statusStyle = statusColors[row.status] || statusColors.Draft;
  const priorityStyle = priorityColors[row.priority] || priorityColors.Normal;
  const taskReference = row._raw?.taskNumber || row._raw?.reference || String(row.id || row._id || '').slice(-8);
  const comments = row._raw?.comments || [];
  const auditHistory = row._raw?.auditHistory || [];
  const attachments = row._raw?.attachments || [];
  const availableDependencyOptions = dependencyOptions.filter(
    (option) =>
      String(option.value) !== String(row.id || row._id) &&
      !createsDependencyCycle(row.id || row._id, option.value, dependencyOptions)
  );
  const auditTaskOptions = [
    ...dependencyOptions,
    ...(row?._raw?.dependencies || []).map((dependency) => ({
      value: dependency?._id || dependency,
      label: dependency?.title
    }))
  ];
  const selectedDependencyOptions = selectedDependencyIds.map((dependencyId) => {
    const matchingOption = auditTaskOptions.find((option) => String(option.value) === String(dependencyId));
    return matchingOption || { value: dependencyId, label: 'Unknown task' };
  });
  const activityEntries = [
    ...comments.map((comment, index) => ({
      kind: 'comment',
      date: comment.createdDate,
      key: `comment-${comment._id || index}`,
      value: comment
    })),
    ...auditHistory
      .filter((entry) => entry.action !== 'Comment added')
      .map((entry, index) => ({
        kind: 'audit',
        date: entry.changedDate,
        key: `audit-${index}`,
        value: entry
      }))
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const handleAddComment = async () => {
    if (isWorking || (!newComment.trim() && commentFiles.length === 0)) return;
    setIsWorking(true);
    try {
      const res = await taskService.addComment(row.id || row._id, newComment.trim(), commentFiles);
      const updated = res?.data?.data;
      if (updated && onUpdateRow) onUpdateRow(mapTaskToRow(updated));
      setNewComment('');
      setCommentFiles([]);
      toast.success('Comment added');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not add comment');
    } finally {
      setIsWorking(false);
    }
  };

  const handleCommentFileSelection = (event) => {
    const files = Array.from(event.target.files || []);
    const oversizedFile = files.find((file) => file.size > 15 * 1024 * 1024);
    if (oversizedFile) {
      toast.error(`${oversizedFile.name} is larger than 15 MB`);
      event.target.value = '';
      return;
    }
    setCommentFiles(files.slice(0, 10));
  };

  const handleSaveTaskStatus = async () => {
    if (selectedStatus === 'Assigned' && !selectedAssigneeId) {
      toast.error('Choose an assignee for an Assigned task');
      return;
    }
    if (selectedStatus === 'Pending' && (!pendingReason || !pendingComment.trim())) {
      toast.error('Select a pending reason and add a comment');
      return;
    }
    if (selectedStatus === 'Cancelled' && !cancellationReason.trim()) {
      toast.error('Add a cancellation reason');
      return;
    }
    if (selectedStatus === 'Complete' && (actualHours === '' || Number(actualHours) < 0)) {
      toast.error('Enter actual hours before completing the task');
      return;
    }

    const workflow = {};
    if (selectedStatus === 'Pending') {
      workflow.pendingReason = pendingReason;
      workflow.pendingComment = pendingComment.trim();
    }
    if (selectedStatus === 'Cancelled') workflow.cancellationReason = cancellationReason.trim();
    const changes = {
      ...(selectedStatus !== row.status ? { status: selectedStatus } : {}),
      ...(selectedStatus === 'Draft' ? { assignedTo: [] } : {}),
      ...(selectedStatus === 'Complete' ? { actualHours: Number(actualHours) } : {}),
      ...(Object.keys(workflow).length ? { workflow } : {})
    };
    if (!Object.keys(changes).length) {
      toast('No status change to save');
      return;
    }

    setIsWorking(true);
    try {
      const response = await taskService.updateTask(row.id || row._id, changes, 'totalTask');
      const updated = response?.data?.data;
      if (updated && onUpdateRow) onUpdateRow(mapTaskToRow(updated));
      toast.success('Task status updated');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not update task status');
    } finally {
      setIsWorking(false);
    }
  };

  const handleSaveTaskEdit = async () => {
    if (!selectedDueDate) {
      toast.error('Choose a due date');
      return;
    }
    if (selectedPriority === 'Critical' && !criticalReason.trim()) {
      toast.error('Select a critical priority reason');
      return;
    }
    if (
      selectedEstimatedHours !== '' &&
      (!Number.isFinite(Number(selectedEstimatedHours)) || Number(selectedEstimatedHours) < 0)
    ) {
      toast.error('Estimated hours must be zero or greater');
      return;
    }
    if (row.status === 'Complete' && (actualHours === '' || Number(actualHours) < 0)) {
      toast.error('Actual hours must be zero or greater');
      return;
    }

    setIsWorking(true);
    try {
      const currentAssigneeId =
        row?._raw?.assignedTo?.[0]?.employeeId?._id || row?._raw?.assignedTo?.[0]?.employeeId || '';
      const currentDueDate = toDateInputValue(row?._raw?.dueDate);
      const currentSupportingStaffOptions = taskSupportingStaffOptions(row?._raw);
      const staffRecipients = splitSupportingStaffOptions(selectedSupportingStaffOptions);
      const currentDependencyIds = (row?.dependencyIds || []).map(String).sort();
      const nextDependencyIds = selectedDependencyIds.map(String).sort();
      const currentClassId = row?._raw?.class?.id || '';
      const selectedClass = classOptions.find((option) => String(option.value) === String(selectedClassId));
      const workflow = {};
      if (selectedPriority === 'Critical' && criticalReason !== (row?._raw?.workflow?.criticalReason || '')) {
        workflow.criticalReason = criticalReason;
      }

      const changes = {
        ...(selectedAssigneeId !== String(currentAssigneeId)
          ? {
              assignedTo: selectedAssigneeId
                ? [{ employeeId: selectedAssigneeId, assignedDate: new Date().toISOString() }]
                : [],
              ...(selectedStatus !== row.status ? { status: selectedStatus } : {})
            }
          : {}),
        ...(selectedDueDate !== currentDueDate ? { dueDate: selectedDueDate } : {}),
        ...(!sameSupportingStaffOptions(currentSupportingStaffOptions, selectedSupportingStaffOptions)
          ? {
              supportingStaff: staffRecipients.supportingStaff,
              externalRecipients: staffRecipients.externalRecipients
            }
          : {}),
        ...(selectedPriority !== row.priority ? { priority: selectedPriority } : {}),
        ...(selectedType !== row.type ? { type: selectedType } : {}),
        ...(selectedDescription !== (row?._raw?.description || '') ? { description: selectedDescription } : {}),
        ...(selectedLocation !== (row?._raw?.location || '') ? { location: selectedLocation } : {}),
        ...(String(selectedEstimatedHours) !== String(row?._raw?.estimatedHours ?? '')
          ? { estimatedHours: selectedEstimatedHours === '' ? null : Number(selectedEstimatedHours) }
          : {}),
        ...(selectedStartTime !== (row?._raw?.startTime || '') ? { startTime: selectedStartTime || null } : {}),
        ...(row.status === 'Complete' && Number(actualHours) !== Number(row?._raw?.actualHours)
          ? { actualHours: Number(actualHours) }
          : {}),
        ...(String(selectedClassId) !== String(currentClassId)
          ? { class: selectedClass ? { id: selectedClass.value, name: selectedClass.label } : null }
          : {}),
        ...(currentDependencyIds.join(',') !== nextDependencyIds.join(',')
          ? { dependencies: selectedDependencyIds }
          : {}),
        ...(Object.keys(workflow).length ? { workflow } : {})
      };

      if (!Object.keys(changes).length) {
        toast('No changes to save');
        setIsWorking(false);
        return;
      }

      const response = await taskService.updateTask(row.id || row._id, changes, 'totalTask');
      const updated = response?.data?.data;
      if (updated && onUpdateRow) onUpdateRow(mapTaskToRow(updated));
      toast.success('Task updated');
      setTaskTabIndex(0);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update task');
    } finally {
      setIsWorking(false);
    }
  };

  const handleAssigneeChange = (event) => {
    const employeeId = event.target.value;
    setSelectedAssigneeId(employeeId);
    if (employeeId && selectedStatus === 'Draft') setSelectedStatus('Assigned');
    if (!employeeId && selectedStatus === 'Assigned') setSelectedStatus('Draft');
  };

  const handleApprove = async () => {
    if (actualHours === '' || Number(actualHours) < 0) {
      toast.error('Enter actual hours before approving the task');
      return;
    }
    setIsWorking(true);
    try {
      if (Number(actualHours) !== Number(row?._raw?.actualHours)) {
        await taskService.updateTask(row.id || row._id, { actualHours: Number(actualHours) }, 'totalTask');
      }
      const res = await taskService.approveTask(row.id || row._id, {
        approvalNotes
      });
      const updated = res?.data?.data;
      if (updated && onUpdateRow) onUpdateRow(mapTaskToRow(updated));
      toast.success('Task accepted and marked Complete');
      onApplyChanges();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not approve task');
    } finally {
      setIsWorking(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setIsWorking(true);
    try {
      const res = await taskService.rejectTask(row.id || row._id, {
        rejectionReason: rejectionReason.trim()
      });
      const updated = res?.data?.data;
      if (updated && onUpdateRow) onUpdateRow(mapTaskToRow(updated));
      toast.success('Task returned to Assigned');
      onApplyChanges();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not reject task');
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancelChanges}
      aria-labelledby="total-task-dialog-title"
      fullWidth
      maxWidth="md"
      PaperComponent={DraggablePaper}
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, sm: 3 },
          height: { xs: 'calc(100dvh - 32px)', sm: 'min(900px, 92vh)' },
          maxHeight: { xs: 'calc(100dvh - 32px)', sm: '92vh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        id="total-task-dialog-title"
        sx={{ px: { xs: 2, sm: 3.5 }, pt: 2.5, pb: 1, cursor: 'move', userSelect: 'none' }}
      >
        <IconButton aria-label="Close task details" onClick={onCancelChanges} edge="start" sx={{ mb: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap'
          }}
        >
          <Typography variant="h5" component="h2" sx={{ flex: 1, minWidth: 220, fontWeight: 700 }}>
            {row.title || 'Task Detail'}
          </Typography>
          {taskReference && (
            <Chip
              label={taskReference}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 1, fontFamily: 'monospace', fontWeight: 600 }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3.5 },
          pb: 3,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1px 1fr' },
            alignItems: 'stretch',
            gap: { xs: 2.5, md: 4 },
            py: 2
          }}
        >
          {/* left container */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DetailItem icon={<PersonOutlineIcon />} label="Assignees">
              <PersonAvatar name={row.assignedTo} />
            </DetailItem>
            <DetailItem icon={<PersonOutlineIcon />} label="Supporting Staff">
              {row.supportingStaff || '—'}
            </DetailItem>
            <DetailItem icon={<CategoryOutlinedIcon />} label="Dependencies">
              {row.dependencies || 'None'}
            </DetailItem>
            <DetailItem icon={<FolderOutlinedIcon />} label="Project">
              {row.projectName}
            </DetailItem>
            <DetailItem icon={<CategoryOutlinedIcon />} label="Type">
              {row.type}
            </DetailItem>
            <DetailItem icon={<CategoryOutlinedIcon />} label="Class">
              {row.class}
            </DetailItem>
            <DetailItem icon={<PlaceOutlinedIcon />} label="Location">
              {row.location}
            </DetailItem>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: 'none', md: 'block' },
              alignSelf: 'stretch',
              height: '100% !important'
            }}
          />

          {/* right container */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DetailItem icon={<FlagOutlinedIcon />} label="Status" style={{ display: 'flex', flexDirection: 'column' }}>
              <StatusIcon status={row.status || 'Draft'} statusStyle={statusStyle} />
            </DetailItem>
            <DetailItem icon={<BoltOutlinedIcon />} label="Priority">
              <Chip
                label={row.priority || 'Normal'}
                size="small"
                sx={{
                  height: 24,
                  borderRadius: 1,
                  fontWeight: 700,
                  ...priorityStyle
                }}
              />
            </DetailItem>
            <DetailItem icon={<PersonOutlineIcon />} label="Created By">
              {row.createdBy}
            </DetailItem>
            <DetailItem icon={<CalendarTodayOutlinedIcon />} label="Created Date">
              {row.createdDate}
            </DetailItem>
            <DetailItem icon={<CalendarTodayOutlinedIcon />} label="Due Date">
              {formatDateDDMMYYYY(row?._raw?.dueDate || row.dueDate)}
            </DetailItem>
            <DetailItem icon={<AccessTimeOutlinedIcon />} label="Est. Hours">
              {row.estimatedHours}
            </DetailItem>
            <DetailItem icon={<AccessTimeOutlinedIcon />} label="Actual Hours">
              {row.actualHours === '' ? '—' : row.actualHours}
            </DetailItem>
            <DetailItem icon={<AccessTimeOutlinedIcon />} label="Start Time">
              {row.startTime === '' ? '—' : row.startTime}
            </DetailItem>
          </Box>
        </Box>

        <Box sx={{ pb: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {row.description || 'No description provided.'}
          </Typography>
          <AttachmentList attachments={attachments} />

          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mt: 2.5,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper'
            }}
          >
            <Tabs
              value={taskTabIndex}
              onChange={(_, value) => setTaskTabIndex(value)}
              aria-label="Task activity and actions"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 42,
                '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontSize: 16 }
              }}
            >
              <Tab label={`Comments and activity${comments.length ? ` (${comments.length})` : ''}`} />
              <Tab label="Task Status" />
              <Tab label="Task Edit" />
              {isTaskCreator && <Tab label="Accept / Reject" />}
            </Tabs>
          </Box>

          {taskTabIndex === 1 && (
            <Box sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Task status
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel id="admin-status-label">Status</InputLabel>
                <Select
                  labelId="admin-status-label"
                  value={selectedStatus}
                  label="Status"
                  onChange={(event) => {
                    const status = event.target.value;
                    setSelectedStatus(status);
                    if (status === 'Draft') setSelectedAssigneeId('');
                  }}
                >
                  {TASK_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedStatus === 'Pending' && (
                <Box sx={{ display: 'grid', gap: 1.5, mt: 1.5 }}>
                  <FormControl size="small" fullWidth required>
                    <InputLabel id="admin-status-pending-reason-label">Pending reason</InputLabel>
                    <Select
                      labelId="admin-status-pending-reason-label"
                      value={pendingReason}
                      label="Pending reason"
                      onChange={(event) => setPendingReason(event.target.value)}
                    >
                      {TASK_PENDING_REASONS.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    size="small"
                    label="Pending comment"
                    value={pendingComment}
                    onChange={(event) => setPendingComment(event.target.value)}
                  />
                </Box>
              )}
              {selectedStatus === 'Cancelled' && (
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  size="small"
                  label="Cancellation reason"
                  value={cancellationReason}
                  onChange={(event) => setCancellationReason(event.target.value)}
                  sx={{ mt: 1.5 }}
                />
              )}
              {selectedStatus === 'Complete' && (
                <TextField
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 0, step: 0.25 }}
                  size="small"
                  label="Actual hours"
                  value={actualHours}
                  onChange={(event) => setActualHours(event.target.value)}
                  sx={{ mt: 1.5 }}
                />
              )}
              <Button
                variant="contained"
                onClick={handleSaveTaskStatus}
                disabled={isWorking}
                startIcon={isWorking ? <CircularProgress size={14} /> : <SaveOutlinedIcon />}
                sx={{ mt: 2.5, textTransform: 'none' }}
              >
                Save Task Status
              </Button>
            </Box>
          )}

          {taskTabIndex === 2 && (
            <Box sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Edit task
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Assign or reassign this task and update its due date, priority, type, class, dependencies, and details.
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="task-assignee-label">Assignee</InputLabel>
                  <Select
                    labelId="task-assignee-label"
                    value={selectedAssigneeId}
                    label="Assignee"
                    onChange={handleAssigneeChange}
                  >
                    <MenuItem value="">No assignee</MenuItem>
                    {employeeOptions.map((employee) => {
                      const employeeId = employee._id || employee.Id;
                      const employeeName =
                        employee.DisplayName || [employee.FirstName, employee.FamilyName].filter(Boolean).join(' ');
                      return (
                        <MenuItem key={employeeId} value={employeeId}>
                          {employeeName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <Box>
                  <InputLabel>Supporting Staff</InputLabel>
                  <SupportingStaffSelect
                    inputId="task-supporting-staff"
                    employees={employeeOptions}
                    externalContacts={externalContacts}
                    value={selectedSupportingStaffOptions}
                    onChange={setSelectedSupportingStaffOptions}
                  />
                </Box>

                <FormControl size="small" fullWidth>
                  <InputLabel id="task-type-label">Type</InputLabel>
                  <Select
                    labelId="task-type-label"
                    value={selectedType}
                    label="Type"
                    onChange={(event) => setSelectedType(event.target.value)}
                  >
                    {TASK_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel id="task-class-label">Class</InputLabel>
                  <Select
                    labelId="task-class-label"
                    value={selectedClassId}
                    label="Class"
                    onChange={(event) => setSelectedClassId(event.target.value)}
                  >
                    <MenuItem value="">No class</MenuItem>
                    {classOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  required
                  size="small"
                  type="date"
                  label="Due date"
                  value={selectedDueDate}
                  onChange={(event) => setSelectedDueDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel id="task-priority-label">Priority</InputLabel>
                  <Select
                    labelId="task-priority-label"
                    value={selectedPriority}
                    label="Priority"
                    onChange={(event) => setSelectedPriority(event.target.value)}
                  >
                    {TASK_PRIORITIES.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <ReactSelect
                    inputId="admin-task-dependencies"
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    options={availableDependencyOptions}
                    value={selectedDependencyOptions}
                    onChange={(options) => setSelectedDependencyIds((options || []).map((option) => option.value))}
                    placeholder="Select dependencies..."
                    styles={dependencySelectStyles}
                    // eslint-disable-next-line no-undef
                    menuPortalTarget={document.body}
                  />
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Description"
                  value={selectedDescription}
                  onChange={(event) => setSelectedDescription(event.target.value)}
                  sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  value={selectedLocation}
                  onChange={(event) => setSelectedLocation(event.target.value)}
                />

                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.25 }}
                  size="small"
                  label="Estimated hours"
                  value={selectedEstimatedHours}
                  onChange={(event) => setSelectedEstimatedHours(event.target.value)}
                />

                <TextField
                  fullWidth
                  type="time"
                  size="small"
                  label="Start Time"
                  value={selectedStartTime}
                  onChange={(event) => setSelectedStartTime(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                />

                {row.status === 'Complete' && (
                  <TextField
                    fullWidth
                    required
                    type="number"
                    inputProps={{ min: 0, step: 0.25 }}
                    size="small"
                    label="Actual hours"
                    value={actualHours}
                    onChange={(event) => setActualHours(event.target.value)}
                  />
                )}
              </Box>

              {selectedPriority === 'Critical' && (
                <FormControl size="small" fullWidth required sx={{ mt: 1.5 }}>
                  <InputLabel id="critical-reason-label">Critical reason</InputLabel>
                  <Select
                    labelId="critical-reason-label"
                    value={criticalReason}
                    label="Critical reason"
                    onChange={(event) => setCriticalReason(event.target.value)}
                  >
                    {TASK_CRITICAL_REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveTaskEdit}
                  disabled={isWorking}
                  startIcon={isWorking ? <CircularProgress size={14} /> : <SaveOutlinedIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Save changes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setTaskTabIndex(0)}
                  disabled={isWorking}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {taskTabIndex === 0 && (
          <Box sx={{ pt: 2 }}>
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 1.5,
                mt: 2
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                placeholder="Enter your comment"
                value={newComment}
                onChange={(event) => setNewComment(event.target.value)}
              />
              {commentFiles.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {commentFiles.map((file) => file.name).join(', ')}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 1 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileOutlinedIcon />}
                  disabled={isWorking}
                  sx={{ textTransform: 'none' }}
                >
                  Attach files
                  <input
                    hidden
                    multiple
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    onChange={handleCommentFileSelection}
                  />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={isWorking || (!newComment.trim() && commentFiles.length === 0)}
                  endIcon={isWorking ? <CircularProgress size={14} /> : <SendOutlinedIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Send
                </Button>
              </Box>
            </Box>
            {!activityEntries.length && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No comments or activity yet.
              </Typography>
            )}
            {activityEntries.map((activity) => {
              if (activity.kind === 'comment') {
                const comment = activity.value;
                const author = comment.createdBy?.DisplayName || comment.createdBy?.FirstName || 'Unknown';
                return (
                  <Box key={activity.key} sx={{ display: 'flex', gap: 1.5, py: 1.75 }}>
                    <PersonAvatar name={author} showName={false} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {author} |{' '}
                        <Typography variant="caption" color="text.secondary">
                          {activity.date ? new Date(activity.date).toLocaleString() : ''}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {comment.comment}
                      </Typography>
                      <AttachmentList attachments={comment.attachments || []} />
                    </Box>
                  </Box>
                );
              }
              const entry = activity.value;
              if (entry.action?.startsWith('Notification sent via whatsapp to ')) {
                return (
                  <Typography
                    key={activity.key}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', py: 0.75 }}
                  >
                    {entry.action} on {entry.changedDate ? new Date(entry.changedDate).toLocaleString() : ''}.
                  </Typography>
                );
              }
              return (
                <Box key={activity.key} sx={{ borderLeft: '3px solid', borderColor: 'primary.light', pl: 2, py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {entry.action}
                  </Typography>
                  {entry.previousValue !== undefined && entry.previousValue !== null && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatAuditValue(entry.action, entry.previousValue, employeeOptions, auditTaskOptions)} →{' '}
                      {formatAuditValue(entry.action, entry.newValue, employeeOptions, auditTaskOptions)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Changed By: {getDisplayName(entry.changedBy, employeeOptions) || 'System'} ·{' '}
                    {activity.date ? new Date(activity.date).toLocaleString() : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {taskTabIndex === 3 && isTaskCreator && (
          <Box sx={{ pt: 2 }}>
            {!isSubmittedForApproval && (
              <Alert severity="info">
                Accept or Reject becomes available after the task is submitted for approval.
              </Alert>
            )}
            {isSubmittedForApproval && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Review this task, then accept it or return it to the assignee.
                </Typography>
                <TextField
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 0, step: 0.25 }}
                  size="small"
                  label="Actual hours"
                  value={actualHours}
                  onChange={(event) => setActualHours(event.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Approval notes (optional)"
                  value={approvalNotes}
                  onChange={(event) => setApprovalNotes(event.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleApprove}
                  disabled={isWorking}
                  sx={{ mt: 1.5, textTransform: 'none' }}
                >
                  Accept &amp; Mark Complete
                </Button>
                <Divider sx={{ my: 3 }} />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Rejection reason"
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                />
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleReject}
                  disabled={isWorking || !rejectionReason.trim()}
                  sx={{ mt: 1.5, textTransform: 'none' }}
                >
                  Reject &amp; Return to Assigned
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// The grid keeps the selected row ID in EditingState. This plugin resolves that
// ID to its current row and renders the read-only task details popup.
// eslint-disable-next-line react/display-name
export const PopupEditing = React.memo(
  ({ popupComponent: Popup, taskTabIndex, setTaskTabIndex, onUpdateRow, classOptions, dependencyOptions }) => (
    <Plugin>
      <Template name="popupEditing">
        <TemplateConnector>
          {({ rows, getRowId, editingRowIds }, { stopEditRows, cancelChangedRows }) => {
            const rowId = editingRowIds[0];
            const editedRow = rows.find((row) => getRowId(row) === rowId);
            const rowIds = rowId === undefined ? [] : [rowId];

            const closePopup = () => {
              if (rowIds.length) {
                stopEditRows({ rowIds });
                cancelChangedRows({ rowIds });
              }
              setTaskTabIndex(0);
            };

            return (
              <Popup
                open={Boolean(editedRow)}
                row={editedRow}
                onApplyChanges={closePopup}
                onCancelChanges={closePopup}
                taskTabIndex={taskTabIndex}
                setTaskTabIndex={setTaskTabIndex}
                onUpdateRow={onUpdateRow}
                classOptions={classOptions}
                dependencyOptions={dependencyOptions}
              />
            );
          }}
        </TemplateConnector>
      </Template>
      <Template name="root">
        <TemplatePlaceholder />
        <TemplatePlaceholder name="popupEditing" />
      </Template>
    </Plugin>
  )
);
